import { type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { useDraggable } from "@dnd-kit/core";

import { cn } from "@fitness-app/utils/src/styles/cn";

// Chip pilnuje wysokości i ucinania, żeby długa nazwa nie wjeżdżała na sąsiedni dzień.
// Padding siedzi na dziecku, nie na chipie: dziecko bywa triggerem popovera i musi pokryć
// cały chip, inaczej najazd na górną/dolną krawędź nie otwiera dymka.
export const CHIP_CLASS_NAME = cn(
  "flex h-6 w-full min-w-0 items-stretch overflow-hidden whitespace-nowrap rounded-md border border-solid",
  "text-xs font-medium leading-none transition-colors select-none",
  "outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
  "[&>*]:flex [&>*]:min-w-0 [&>*]:flex-1 [&>*]:items-center [&>*]:px-1.5",
  "[&_svg]:shrink-0 [&_.anticon]:shrink-0 [&_.ant-typography]:text-xs [&_.ant-typography]:text-inherit",
  // Chip z otwartym dymkiem dostaje obwódkę, żeby było widać, czego dymek dotyczy.
  "has-[.ant-popover-open]:ring-2 has-[.ant-popover-open]:ring-blue-400/70",
);

interface CalendarChipProps {
  dragId: string;
  /** Trafia do `active.data.current` w `CalendarDndContext`. */
  dragData: Record<string, unknown>;
  canDrag: boolean;
  toneClassName: string;
  onClick?: () => void;
  title?: string;
  children: ReactNode;
}

export const CalendarChip = ({
  dragId,
  dragData,
  canDrag,
  toneClassName,
  onClick,
  title,
  children,
}: CalendarChipProps) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    data: dragData,
    disabled: !canDrag,
  });

  // Popovery w treści chipa renderują się w portalu, a zdarzenia Reacta i tak bąbelkują przez
  // portal do chipa. Bez sprawdzenia DOM klik w „Usuń" w popoverze otwierałby edycję,
  // a zaznaczanie tekstu w popoverze zaczynało przeciąganie.
  const isFromChip = (e: MouseEvent<HTMLDivElement> | PointerEvent<HTMLDivElement>) =>
    e.currentTarget.contains(e.target as Node);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-calendar-chip=""
      title={title}
      onPointerDown={(e) => {
        if (isFromChip(e)) {
          listeners?.onPointerDown?.(e);
        }
      }}
      onClick={(e) => {
        if (isFromChip(e)) {
          onClick?.();
        }
      }}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      className={cn(
        CHIP_CLASS_NAME,
        toneClassName,
        canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
        isDragging && "opacity-40",
      )}
      style={canDrag ? { touchAction: "none" } : undefined}
    >
      {children}
    </div>
  );
};

export const CalendarChipPreview = ({ toneClassName, children }: { toneClassName: string; children: ReactNode }) => (
  <div className={cn(CHIP_CLASS_NAME, toneClassName, "w-48 cursor-grabbing shadow-lg ring-1 ring-black/5")}>
    {children}
  </div>
);
